import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';

const GestionInvoices = () => {
  const { user, API } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [reenviando, setReenviando] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [eliminando, setEliminando] = useState(false);
  
  // Modal de factura manual
  const [showModal, setShowModal] = useState(false);
  const [estudiantes, setEstudiantes] = useState([]);
  const [semestres, setSemestres] = useState([]);
  const [generando, setGenerando] = useState(false);
  const [formData, setFormData] = useState({
    estudiante_id: '',
    semestre_id: '',
    moneda: 'UYU',
    metodo_pago: 'efectivo',
    descuento_porcentaje: 0,
    descuento_motivo: '',
    enviar_email: true
  });
  const [busquedaEst, setBusquedaEst] = useState('');

  useEffect(() => {
    if (user?.rol === 'admin') {
      loadInvoices();
    }
  }, [user]);

  useEffect(() => {
    filterInvoices();
  }, [filtroEstado, busqueda, invoices]);

  const loadInvoices = async () => {
    try {
      const response = await axios.get(`${API}/admin/invoices`);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  const filterInvoices = () => {
    let filtered = [...invoices];
    
    // Filtro por estado
    if (filtroEstado !== 'todos') {
      filtered = filtered.filter(inv => inv.estado === filtroEstado);
    }
    
    // Búsqueda por nombre o número
    if (busqueda) {
      const searchLower = busqueda.toLowerCase();
      filtered = filtered.filter(inv => 
        inv.estudiante_nombre.toLowerCase().includes(searchLower) ||
        inv.numero_invoice.toLowerCase().includes(searchLower) ||
        inv.estudiante_email.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredInvoices(filtered);
  };

  const handleReenviar = async (invoiceNumber) => {
    if (!window.confirm('¿Estás seguro de reenviar este invoice al estudiante?')) {
      return;
    }
    
    setReenviando(invoiceNumber);
    try {
      await axios.post(`${API}/invoices/${invoiceNumber}/reenviar`);
      toast.success('Invoice reenviado exitosamente');
      await loadInvoices();
    } catch (error) {
      console.error('Error resending invoice:', error);
      toast.error('Error al reenviar invoice');
    } finally {
      setReenviando(null);
    }
  };

  const handleMarcarPagado = async (invoice) => {
    if (!invoice.registro_id) {
      toast.error('Este invoice no tiene registro asociado');
      return;
    }
    const nombre = invoice.estudiante_nombre || invoice.estudiante_email;
    if (!window.confirm(
      `¿Confirmar que recibiste el pago de ${nombre} por WhatsApp?\n\n` +
      `Factura: ${invoice.numero_invoice}\n` +
      `Monto: ${invoice.moneda} ${(invoice.monto_total || invoice.monto || 0).toFixed(2)}\n\n` +
      `Al confirmar:\n` +
      `• Se marcará la factura como PAGADA\n` +
      `• El estudiante quedará inscrito automáticamente en las clases\n` +
      `• Se le enviará el recibo por email\n` +
      `• Recibirá notificación en la app`
    )) return;
    setReenviando(invoice.numero_invoice);
    try {
      await axios.post(`${API}/admin/registraciones/${invoice.registro_id}/confirmar-whatsapp`);
      toast.success(`Pago confirmado — ${nombre} inscrito a sus clases`);
      await loadInvoices();
    } catch (error) {
      console.error('Error confirmando pago:', error);
      toast.error(error.response?.data?.detail || 'Error al marcar como pagado');
    } finally {
      setReenviando(null);
    }
  };

  const handleDescargar = async (invoiceNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/${invoiceNumber}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando invoice:', error);
      toast.error('Error al descargar la factura');
    }
  };

  const handleSelectInvoice = (invoiceNumber) => {
    setSelectedInvoices(prev => {
      if (prev.includes(invoiceNumber)) {
        return prev.filter(num => num !== invoiceNumber);
      } else {
        return [...prev, invoiceNumber];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === filteredInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(filteredInvoices.map(inv => inv.numero_invoice));
    }
  };

  const handleEliminarSeleccionados = async () => {
    if (selectedInvoices.length === 0) {
      toast.error('No hay invoices seleccionados');
      return;
    }

    const confirmMessage = selectedInvoices.length === 1
      ? '¿Estás seguro de eliminar este invoice? Esta acción no se puede deshacer.'
      : `¿Estás seguro de eliminar ${selectedInvoices.length} invoices? Esta acción no se puede deshacer.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setEliminando(true);
    let eliminados = 0;
    let errores = 0;

    try {
      for (const invoiceNumber of selectedInvoices) {
        try {
          await axios.delete(`${API}/admin/invoices/${invoiceNumber}`);
          eliminados++;
        } catch (error) {
          console.error(`Error eliminando ${invoiceNumber}:`, error);
          errores++;
        }
      }

      if (eliminados > 0) {
        toast.success(`${eliminados} invoice(s) eliminado(s) exitosamente`);
        setSelectedInvoices([]);
        await loadInvoices();
      }

      if (errores > 0) {
        toast.error(`${errores} invoice(s) no pudieron ser eliminados`);
      }
    } catch (error) {
      console.error('Error en eliminación masiva:', error);
      toast.error('Error al eliminar invoices');
    } finally {
      setEliminando(false);
    }
  };

  const getEstadoBadge = (estado, fechaVencimiento) => {
    const ahora = new Date();
    const vencimiento = new Date(fechaVencimiento);
    
    if (estado === 'pagado') {
      return <Badge className="bg-green-100 text-green-800">Pagado</Badge>;
    } else if (ahora > vencimiento) {
      return <Badge className="bg-red-100 text-red-800">Vencido</Badge>;
    } else {
      return <Badge className="bg-yellow-100 text-yellow-800">Pendiente</Badge>;
    }
  };

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-UY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const countByEstado = (estado) => {
    if (estado === 'todos') return invoices.length;
    if (estado === 'vencidos') {
      const ahora = new Date();
      return invoices.filter(inv => 
        inv.estado === 'pendiente' && new Date(inv.fecha_vencimiento) < ahora
      ).length;
    }
    return invoices.filter(inv => inv.estado === estado).length;
  };

  // --- Factura Manual ---
  const openModalFactura = async () => {
    setShowModal(true);
    setFormData({ estudiante_id: '', semestre_id: '', moneda: 'UYU', metodo_pago: 'efectivo', descuento_porcentaje: 0, descuento_motivo: '', enviar_email: true });
    setBusquedaEst('');
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/pipeline-registro`, { headers });
      const pipeline = res.data.pipeline || {};
      const allStudents = [];
      Object.values(pipeline).forEach(arr => arr.forEach(s => {
        if (!allStudents.find(x => x.id === s.id)) allStudents.push(s);
      }));
      setEstudiantes(allStudents);
      setSemestres(res.data.semestres || []);
    } catch (e) {
      toast.error('Error cargando datos');
    }
  };

  const selectedStudent = estudiantes.find(e => e.id === formData.estudiante_id);
  const selectedSemester = semestres.find(s => s.id === formData.semestre_id);

  const filteredEstudiantes = busquedaEst.trim()
    ? estudiantes.filter(e => (e.nombre || '').toLowerCase().includes(busquedaEst.toLowerCase()) || (e.email || '').toLowerCase().includes(busquedaEst.toLowerCase()))
    : estudiantes;

  // Calcular preview de monto
  const montoBase = selectedSemester ? (formData.moneda === 'UYU' ? selectedSemester.price_uyu : selectedSemester.price_usd) : 0;
  const descuentoMonto = formData.metodo_pago === 'estudiante_becado' ? montoBase : (montoBase * formData.descuento_porcentaje / 100);
  const montoFinal = formData.metodo_pago === 'estudiante_becado' ? 0 : (montoBase - descuentoMonto);

  const handleGenerarFactura = async () => {
    if (!formData.estudiante_id || !formData.semestre_id) {
      toast.error('Selecciona estudiante y semestre');
      return;
    }
    setGenerando(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/invoices/generar-manual`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(res.data.message);
        setShowModal(false);
        loadInvoices();
        if (res.data.invoice_numero) {
          setTimeout(() => handleDescargar(res.data.invoice_numero), 1000);
        }
      } else {
        toast.error(res.data.message || 'Error al generar factura');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar factura');
    } finally {
      setGenerando(false);
    }
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="shadow-lg border-red-200">
          <CardContent className="text-center py-12">
            <i className="fas fa-lock text-red-500 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-red-800 mb-2">Acceso Restringido</h3>
            <p className="text-red-600">Solo administradores pueden gestionar facturas.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
          <p className="text-gray-600">Cargando facturas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              <i className="fas fa-file-invoice mr-3 text-blue-600"></i>
              Gestión de Facturas
            </h1>
            <p className="text-gray-600">
              Administra todas las facturas enviadas a los estudiantes
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              onClick={openModalFactura}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="btn-generar-factura"
            >
              <i className="fas fa-plus mr-2"></i>
              Generar Factura
            </Button>
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              {filteredInvoices.length} facturas
            </Badge>
            {selectedInvoices.length > 0 && (
              <>
                <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                  {selectedInvoices.length} seleccionado(s)
                </Badge>
                <Button
                  onClick={handleEliminarSeleccionados}
                  disabled={eliminando}
                  variant="destructive"
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                >
                  {eliminando ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash mr-2"></i>
                      Eliminar Seleccionados
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={filteredInvoices.length > 0 && selectedInvoices.length === filteredInvoices.length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Seleccionar todos
                </span>
              </label>
              <Input
                placeholder="Buscar por nombre, email o número..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="flex-1"
              />
            </div>
            
            {/* Estado Filter */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={filtroEstado === 'todos' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroEstado('todos')}
                className={filtroEstado === 'todos' ? 'bg-blue-600' : ''}
              >
                Todos ({countByEstado('todos')})
              </Button>
              <Button
                variant={filtroEstado === 'pendiente' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroEstado('pendiente')}
                className={filtroEstado === 'pendiente' ? 'bg-yellow-600' : ''}
              >
                Pendientes ({countByEstado('pendiente')})
              </Button>
              <Button
                variant={filtroEstado === 'pagado' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroEstado('pagado')}
                className={filtroEstado === 'pagado' ? 'bg-green-600' : ''}
              >
                Pagados ({countByEstado('pagado')})
              </Button>
              <Button
                variant={filtroEstado === 'vencidos' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroEstado('vencidos')}
                className={filtroEstado === 'vencidos' ? 'bg-red-600' : ''}
              >
                Vencidos ({countByEstado('vencidos')})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <i className="fas fa-inbox text-gray-400 text-6xl mb-4"></i>
            <p className="text-gray-600">No se encontraron facturas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredInvoices.map((invoice) => {
            const ahora = new Date();
            const vencimiento = new Date(invoice.fecha_vencimiento);
            const estaVencido = invoice.estado === 'pendiente' && ahora > vencimiento;
            
            return (
              <Card key={invoice.id} className={`hover:shadow-lg transition-shadow ${estaVencido ? 'border-red-300' : ''} ${selectedInvoices.includes(invoice.numero_invoice) ? 'ring-2 ring-blue-500' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex-1 mb-4 md:mb-0">
                      <div className="flex items-center gap-3 mb-2">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(invoice.numero_invoice)}
                          onChange={() => handleSelectInvoice(invoice.numero_invoice)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        />
                        <h3 className="text-lg font-semibold text-gray-900">
                          {invoice.numero_invoice}
                        </h3>
                        {getEstadoBadge(invoice.estado, invoice.fecha_vencimiento)}
                        {invoice.es_becado && (
                          <Badge className="bg-purple-100 text-purple-800">Becado</Badge>
                        )}
                        {invoice.es_renovacion && (
                          <Badge className="bg-blue-100 text-blue-800">Renovación</Badge>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>
                          <i className="fas fa-user mr-2"></i>
                          <strong>{invoice.estudiante_nombre}</strong> ({invoice.estudiante_email})
                        </p>
                        <p>
                          <i className="fas fa-graduation-cap mr-2"></i>
                          {invoice.semestre_nombre}
                        </p>
                        <p>
                          <i className="fas fa-money-bill-wave mr-2"></i>
                          {invoice.moneda} {(invoice.monto_total || invoice.monto || 0).toFixed(2)}
                        </p>
                        <p>
                          <i className="fas fa-calendar mr-2"></i>
                          Emitido: {formatFecha(invoice.fecha_emision)} | 
                          Vence: {formatFecha(invoice.fecha_vencimiento)}
                          {estaVencido && (
                            <span className="text-red-600 font-semibold ml-2">⚠️ VENCIDO</span>
                          )}
                        </p>
                        {invoice.email_enviado && (
                          <p className="text-green-600">
                            <i className="fas fa-check-circle mr-2"></i>
                            Email enviado: {formatFecha(invoice.fecha_email)}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      {(invoice.estado === 'pendiente' || (invoice.estado !== 'pagado' && ahora > vencimiento)) && (
                        <Button
                          onClick={() => handleMarcarPagado(invoice)}
                          size="sm"
                          disabled={reenviando === invoice.numero_invoice}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`marcar-pagado-btn-${invoice.numero_invoice}`}
                          title="Confirmar que recibiste el pago por WhatsApp"
                        >
                          {reenviando === invoice.numero_invoice ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2"></i>
                              Confirmando...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-check-circle mr-2"></i>
                              Marcar Pagado
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        onClick={() => handleDescargar(invoice.numero_invoice)}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <i className="fas fa-download mr-2"></i>
                        Descargar PDF
                      </Button>
                      <Button
                        onClick={() => handleReenviar(invoice.numero_invoice)}
                        variant="outline"
                        size="sm"
                        disabled={reenviando === invoice.numero_invoice}
                        className="text-purple-600 hover:text-purple-700"
                      >
                        {reenviando === invoice.numero_invoice ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane mr-2"></i>
                            Reenviar Email
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Generar Factura Manual */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-xl p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="modal-generar-factura">
          {/* Header con gradiente */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 text-white flex-shrink-0">
            <DialogTitle className="text-lg font-bold flex items-center gap-2.5 text-white">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                <i className="fas fa-file-invoice text-white"></i>
              </div>
              Generar Factura Manual
            </DialogTitle>
            <p className="text-emerald-100 text-sm mt-1">Crea y envía una factura directamente a un estudiante</p>
          </div>

          <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
            {/* Buscar Estudiante */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2 block">Estudiante</Label>
              {selectedStudent ? (
                <div className="flex items-center justify-between p-3 border-2 border-emerald-200 rounded-xl bg-emerald-50 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-200 flex items-center justify-center">
                      <i className="fas fa-user text-emerald-700 text-sm"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900">{selectedStudent.nombre}</p>
                      <p className="text-xs text-gray-500">{selectedStudent.email}</p>
                    </div>
                  </div>
                  <button onClick={() => setFormData(f => ({ ...f, estudiante_id: '' }))} className="w-7 h-7 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center transition-colors">
                    <i className="fas fa-times text-red-500 text-xs"></i>
                  </button>
                </div>
              ) : (
                <div className="border rounded-xl overflow-hidden">
                  <div className="relative">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <Input
                      placeholder="Buscar por nombre o email..."
                      value={busquedaEst}
                      onChange={e => setBusquedaEst(e.target.value)}
                      className="border-0 border-b rounded-none pl-9 focus:ring-0"
                      data-testid="buscar-estudiante-factura"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {filteredEstudiantes.length === 0 ? (
                      <p className="p-4 text-sm text-gray-400 text-center">No se encontraron estudiantes</p>
                    ) : filteredEstudiantes.map(est => (
                      <button
                        key={est.id}
                        onClick={() => { setFormData(f => ({ ...f, estudiante_id: est.id })); setBusquedaEst(''); }}
                        className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b last:border-b-0 flex items-center gap-3 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-user text-gray-400 text-xs"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{est.nombre}</p>
                          <p className="text-xs text-gray-500 truncate">{est.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Semestre y Moneda en una fila */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2 block">Semestre</Label>
                <Select value={formData.semestre_id} onValueChange={v => setFormData(f => ({ ...f, semestre_id: v }))}>
                  <SelectTrigger data-testid="select-semestre-factura" className="h-11">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {semestres.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.nombre || `Semestre ${s.numero}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2 block">Moneda</Label>
                <Select value={formData.moneda} onValueChange={v => setFormData(f => ({ ...f, moneda: v }))}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UYU">UYU - Pesos Uruguayos</SelectItem>
                    <SelectItem value="USD">USD - Dólares</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Método de Pago */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2 block">Método de Pago</Label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { value: 'efectivo', icon: 'fas fa-money-bill-wave', label: 'Efectivo' },
                  { value: 'transferencia', icon: 'fas fa-university', label: 'Transfer.' },
                  { value: 'tarjeta', icon: 'fas fa-credit-card', label: 'Tarjeta' },
                  { value: 'estudiante_becado', icon: 'fas fa-award', label: 'Becado' },
                ].map(m => (
                  <button
                    key={m.value}
                    onClick={() => setFormData(f => ({ ...f, metodo_pago: m.value }))}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                      formData.metodo_pago === m.value
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-500'
                    }`}
                  >
                    <i className={`${m.icon} text-lg`}></i>
                    <span className="text-[11px] font-medium">{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Descuento */}
            {formData.metodo_pago !== 'estudiante_becado' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2 block">
                  <i className="fas fa-tag mr-1"></i> Descuento
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <Input
                      type="number" min="0" max="100"
                      value={formData.descuento_porcentaje}
                      onChange={e => setFormData(f => ({ ...f, descuento_porcentaje: Number(e.target.value) }))}
                      className="pr-8"
                      data-testid="input-descuento"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">%</span>
                  </div>
                  <Input
                    placeholder="Motivo del descuento"
                    value={formData.descuento_motivo}
                    onChange={e => setFormData(f => ({ ...f, descuento_motivo: e.target.value }))}
                  />
                </div>
              </div>
            )}

            {/* Preview de Monto */}
            {selectedSemester && (
              <div className="bg-gray-900 text-white rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Resumen</p>
                    <p className="text-sm text-gray-300 mt-1">{selectedSemester.nombre || `Semestre ${selectedSemester.numero}`}</p>
                  </div>
                  <div className="text-right">
                    {descuentoMonto > 0 && (
                      <p className="text-xs text-gray-500 line-through">{formData.moneda} {montoBase.toLocaleString()}</p>
                    )}
                    <p className="text-2xl font-black">{formData.moneda} {montoFinal.toLocaleString()}</p>
                    {descuentoMonto > 0 && (
                      <p className="text-xs text-emerald-400">-{formData.descuento_porcentaje}% descuento</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Enviar Email */}
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={formData.enviar_email}
                onChange={e => setFormData(f => ({ ...f, enviar_email: e.target.checked }))}
                className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-800">Enviar factura por email</span>
                <p className="text-xs text-gray-500">El estudiante recibirá la factura en su correo</p>
              </div>
              <i className="fas fa-envelope text-gray-400 ml-auto"></i>
            </label>
          </div>

          {/* Footer - siempre visible */}
          <div className="border-t bg-gray-50 px-6 py-3 flex justify-end gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setShowModal(false)} className="px-5">Cancelar</Button>
            <Button
              onClick={handleGenerarFactura}
              disabled={generando || !formData.estudiante_id || !formData.semestre_id}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 shadow-lg shadow-emerald-200"
              data-testid="btn-confirmar-factura"
            >
              {generando ? (
                <><i className="fas fa-spinner fa-spin mr-2"></i>Generando...</>
              ) : (
                <><i className="fas fa-file-invoice mr-2"></i>Generar y Descargar</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionInvoices;
